// Runtime configuration. The same image serves several hosts (the VPS test host now,
// go.feedbackjourney.com later), so nothing host-specific is baked in at build time:
//
// - Our own URLs (API, invite links) come from the page's origin. VITE_API / VITE_SELF
//   still override them, which `npm run dev` needs because Vite (2001) and the API (2000) differ.
// - Card links point at the website (homepageUrl), which sends /c/* on to the app.
// - Logto settings come from the server (GET /api/public/config, read from its environment), so a
//   new Logto app id or endpoint is a config change, not a rebuild.

export type RuntimeConfig = {
  logtoEndpoint: string;
  logtoAppId: string;
  apiResource: string;
};

const origin = () => window.location.origin;

/** Base URL of the API, without trailing slash, e.g. https://go.feedbackjourney.com/api */
export const apiUrl = (): string => import.meta.env.VITE_API || `${origin()}/api`;

/** Base URL of the app itself, e.g. https://go.feedbackjourney.com */
export const selfUrl = (): string => import.meta.env.VITE_SELF || origin();

/** The marketing site, where logout and the responder form send people. */
export const homepageUrl = (): string => import.meta.env.VITE_FRONTPAGE || "https://feedbackjourney.com";

/**
 * The app, also from the public card page. That page can be loaded from the website's address,
 * where "/" is the website, so it falls back to the same default as APP_URL in the server's cardPage.ts.
 */
export const appUrl = (): string => {
  if (import.meta.env.VITE_SELF) return import.meta.env.VITE_SELF;
  return origin() === homepageUrl() ? "https://go.feedbackjourney.com" : origin();
};

let loaded: RuntimeConfig | null = null;

/** Fetches the config once. Call before rendering anything that needs auth. */
export const loadRuntimeConfig = async (): Promise<RuntimeConfig> => {
  if (loaded) return loaded;
  const res = await fetch(`${apiUrl()}/public/config`);
  if (!res.ok) throw new Error(`config request failed (${res.status})`);
  loaded = (await res.json()) as RuntimeConfig;
  return loaded;
};

export const getRuntimeConfig = (): RuntimeConfig => {
  if (!loaded) throw new Error("runtime config not loaded; call loadRuntimeConfig() first");
  return loaded;
};
